import React, { useState, useEffect } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useUserStats } from '../../hooks/useUserStats';

interface UserStats {
  total_score: number;
  games_played: number;
  high_score: number;
  average_score: number;
  by_game_type: Record<string, {
    games_played: number;
    total_score: number;
    high_score: number;
  }>;
}

const Layout: React.FC = () => {
  const location = useLocation();
  const { stats } = useUserStats();
  
  // Type guard to check if stats is UserStats
  const isUserStats = (stats: any): stats is UserStats => {
    return stats && 
      typeof stats.total_score === 'number' &&
      typeof stats.games_played === 'number' &&
      typeof stats.high_score === 'number' &&
      typeof stats.average_score === 'number' &&
      typeof stats.by_game_type === 'object';
  };

  // Ensure stats is properly typed
  const typedStats = isUserStats(stats) ? stats : undefined;
  
  // Function to check if viewport is mobile
  const isMobileView = () => window.innerWidth <= 768;
  
  // Initialize sidebar state based on viewport
  const [isSidebarActive, setIsSidebarActive] = useState(!isMobileView());
  const [isGameplayRoute, setIsGameplayRoute] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      // Only auto-open sidebar on desktop if not in gameplay
      if (!isGameplayRoute) {
        setIsSidebarActive(!isMobileView());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isGameplayRoute]);

  // Handle route changes
  useEffect(() => {
    const isGameRoute = location.pathname.includes('/game/');
    setIsGameplayRoute(isGameRoute);

    if (isGameRoute) {
      // Always close sidebar in gameplay
      setIsSidebarActive(false);
    } else if (!isMobileView()) {
      // Only open sidebar when leaving gameplay on desktop
      setIsSidebarActive(true);
    } else {
      // Keep sidebar closed on mobile when navigating
      setIsSidebarActive(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarActive(!isSidebarActive);
  };

  return (
    <div className="container">
      <Sidebar 
        isActive={isSidebarActive} 
        userStats={typedStats}
      />
      <main className={`main-content ${isSidebarActive ? 'sidebar-active' : ''}`}>
        <Header onMenuToggle={toggleSidebar} />
        <Outlet />
        <Footer />
      </main>
    </div>
  );
};

export default Layout; 